// src/CustomerSurveyImagesAdmin.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Grid,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { db, storage } from "./firebase";
import { collection, onSnapshot, doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  ART_STYLES,
  COLORS,
  PAINT_TYPES,
  FRAME_OPTIONS,
} from "./customerSurveyOptions";

const CATEGORIES = [
  { key: "styles", label: "🎨 الأسلوب الفني", options: ART_STYLES },
  {
    key: "colors",
    label: "🌈 الألوان",
    options: COLORS.map((c) => ({ key: c.key, label: c.label, hex: c.hex })),
  },
  { key: "paintTypes", label: "🖌️ نوع الرسم", options: PAINT_TYPES },
  { key: "frames", label: "🖼️ الإطارات", options: FRAME_OPTIONS },
];

const CustomerSurveyImagesAdmin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [imagesMap, setImagesMap] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "surveyOptionImages"), (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data().images || [];
      });
      setImagesMap(map);
    });
    return () => unsub();
  }, []);

  const handleUpload = async (optionKey, files) => {
    if (!files.length) return;
    setUploadingKey(optionKey);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const safeName = file.name.replace(/[^\w.\-]/g, "_");
        const storageRef = ref(
          storage,
          `surveyOptionImages/${optionKey}/${Date.now()}-${safeName}`
        );
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        uploadedUrls.push(url);
      }

      const existing = imagesMap[optionKey] || [];
      await setDoc(
        doc(db, "surveyOptionImages", optionKey),
        { images: [...existing, ...uploadedUrls] },
        { merge: true }
      );
    } catch (err) {
      console.error("فشل رفع الصور:", err);
      alert("❌ حدث خطأ أثناء رفع الصور");
    } finally {
      setUploadingKey(null);
    }
  };

  const handleRemoveImage = async (optionKey, urlToRemove) => {
    const existing = imagesMap[optionKey] || [];
    await setDoc(
      doc(db, "surveyOptionImages", optionKey),
      { images: existing.filter((u) => u !== urlToRemove) },
      { merge: true }
    );
  };

  const currentCategory = CATEGORIES[activeTab];

  return (
    <Container sx={{ mt: 4, mb: 6 }} dir="rtl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={900}>
          🖼️ إدارة الصور المرجعية لاستبيان الزبون
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/customer-survey")}>
          العودة للاستبيان
        </Button>
      </Box>

      <Paper sx={{ mb: 3, borderRadius: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {CATEGORIES.map((cat) => (
            <Tab key={cat.key} label={cat.label} />
          ))}
        </Tabs>
      </Paper>

      <Grid container spacing={2}>
        {currentCategory.options.map((opt) => {
          const images = imagesMap[opt.key] || [];
          return (
            <Grid item xs={12} sm={6} md={4} key={opt.key}>
              <Paper sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {opt.hex && (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: opt.hex,
                        border: "1px solid #ccc",
                      }}
                    />
                  )}
                  <Typography fontWeight="bold">{opt.label}</Typography>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                  {images.length === 0 && (
                    <Typography fontSize={12} color="text.secondary">
                      لا توجد صور بعد
                    </Typography>
                  )}
                  {images.map((url, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        position: "relative",
                        width: 80,
                        height: 80,
                        borderRadius: 1.5,
                        overflow: "hidden",
                        border: "1px solid #ddd",
                        background: "#f5f5f5",
                      }}
                    >
                      <img
                        src={url}
                        alt={`${opt.label}-${idx}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(opt.key, url)}
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          bgcolor: "rgba(255,255,255,0.85)",
                          p: 0.3,
                          "&:hover": { bgcolor: "#fff" },
                        }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  fullWidth
                  disabled={uploadingKey === opt.key}
                  startIcon={
                    uploadingKey === opt.key ? (
                      <CircularProgress size={16} />
                    ) : null
                  }
                >
                  {uploadingKey === opt.key ? "جارِ الرفع..." : "📤 رفع صور"}
                  <input
                    hidden
                    multiple
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      handleUpload(opt.key, Array.from(e.target.files || []));
                      e.target.value = "";
                    }}
                  />
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default CustomerSurveyImagesAdmin;
